<template>
    <div class="mailing-telegram">
        <div class="panel">
            <header class="panel__header">
                <div>
                    <h3>Отправка Telegram сообщений</h3>
                    <p class="panel__subtitle">Используется сервис tg-client</p>
                </div>
                <Button icon="pi pi-refresh" class="p-button-text" :loading="loadingAccounts"
                    :disabled="loadingAccounts" @click="fetchAccounts">
                    Обновить аккаунты
                </Button>
            </header>

            <div class="form-grid">
                <div class="field">
                    <label for="account">Аккаунт Telegram</label>
                    <Dropdown id="account" v-model="form.accountId" :options="accountOptions" optionLabel="label"
                        optionValue="value" :loading="loadingAccounts" placeholder="Выберите аккаунт"
                        :disabled="!accountOptions.length" class="w-full" />
                    <small v-if="!accountOptions.length && !loadingAccounts" class="hint">
                        Нет активных сеансов. Подключите аккаунт через tg-client.
                    </small>
                </div>

                <div class="field field--half">
                    <label for="identifierType">Тип идентификатора</label>
                    <Dropdown id="identifierType" v-model="identifierType" :options="identifierOptions"
                        optionLabel="label" optionValue="value" class="w-full" />
                </div>

                <div class="field field--half" v-if="identifierType === 'phone'">
                    <label for="phone">Номер телефона</label>
                    <FloatLabel variant="on">
                        <InputText id="phone" v-model="form.phoneNumber" class="w-full" />
                        <label for="phone">Номер в международном формате</label>
                    </FloatLabel>
                </div>

                <div class="field field--half" v-else-if="identifierType === 'username'">
                    <label for="username">Username</label>
                    <FloatLabel variant="on">
                        <InputText id="username" v-model="form.username" class="w-full" />
                        <label for="username">Например, @username</label>
                    </FloatLabel>
                </div>

                <div class="field field--half" v-else>
                    <label for="chatId">Chat ID</label>
                    <FloatLabel variant="on">
                        <InputText id="chatId" v-model="form.chatId" class="w-full" />
                        <label for="chatId">Например, -1001234567890</label>
                    </FloatLabel>
                </div>

                <template v-if="identifierType === 'phone'">
                    <div class="field field--half checkbox-field">
                        <label>Если номера нет в контактах</label>
                        <ToggleSwitch v-model="form.autoAddContact" inputId="autoAdd" />
                        <label for="autoAdd" class="toggle-label">Автодобавление контакта</label>
                    </div>
                    <div class="field field--half" v-if="form.autoAddContact">
                        <label class="sr-only">Имя контакта</label>
                        <div class="names-row">
                            <FloatLabel variant="on">
                                <InputText id="firstName" v-model="form.firstName" class="w-full"
                                    placeholder="Имя" />
                                <label for="firstName">Имя</label>
                            </FloatLabel>
                            <FloatLabel variant="on">
                                <InputText id="lastName" v-model="form.lastName" class="w-full"
                                    placeholder="Фамилия" />
                                <label for="lastName">Фамилия</label>
                            </FloatLabel>
                        </div>
                    </div>
                </template>

                <div class="field field--full">
                    <label for="message">Текст сообщения</label>
                    <FloatLabel variant="on">
                        <Textarea id="message" v-model="form.message" rows="6" autoResize class="w-full"
                            maxlength="4096" />
                        <label for="message">Введите текст сообщения</label>
                    </FloatLabel>
                    <small class="hint">Максимум 4096 символов (ограничение Telegram)</small>
                </div>

                <div class="field field--full feedback-group">
                    <Message v-if="feedback.type" :severity="feedback.type" :closable="false">
                        {{ feedback.text }}
                    </Message>
                </div>

                <div class="field field--full">
                    <Button class="w-full" :loading="sending" :disabled="sendDisabled"
                        @click="handleSend">
                        {{ sending ? 'Отправляем...' : 'Отправить сообщение' }}
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import Dropdown from 'primevue/dropdown';
import Textarea from 'primevue/textarea';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Message from 'primevue/message';
import ToggleSwitch from 'primevue/toggleswitch';
import FloatLabel from 'primevue/floatlabel';

export default {
    components: {
        Dropdown,
        Textarea,
        InputText,
        Button,
        Message,
        ToggleSwitch,
        FloatLabel
    },
    data() {
        return {
            accounts: [],
            loadingAccounts: false,
            sending: false,
            identifierType: 'phone',
            form: {
                accountId: '',
                phoneNumber: '',
                username: '',
                chatId: '',
                message: '',
                autoAddContact: false,
                firstName: '',
                lastName: ''
            },
            feedback: {
                type: '',
                text: ''
            },
            identifierOptions: [
                { label: 'Номер телефона', value: 'phone' },
                { label: 'Username', value: 'username' },
                { label: 'Chat ID', value: 'chatId' }
            ]
        }
    },
    computed: {
        accountOptions() {
            return this.accounts.map((acc) => ({
                label: this.formatAccountLabel(acc),
                value: acc.accountId
            }));
        },
        sendDisabled() {
            if (!this.form.accountId || !this.form.message.trim()) {
                return true;
            }
            if (this.identifierType === 'phone') {
                return !this.form.phoneNumber.trim();
            }
            if (this.identifierType === 'username') {
                return !this.form.username.trim();
            }
            return !this.form.chatId.trim();
        }
    },
    mounted() {
        this.fetchAccounts();
    },
    methods: {
        formatAccountLabel(acc) {
            if (!acc?.me) {
                return `${acc.accountId}${acc.connected ? '' : ' (offline)'}`;
            }
            const name = [acc.me.firstName, acc.me.lastName].filter(Boolean).join(' ').trim();
            const username = acc.me.username ? `@${acc.me.username}` : '';
            const phone = acc.me.phone ? `+${acc.me.phone}` : '';
            const parts = [name || 'Без имени', username, phone].filter(Boolean);
            return `${parts.join(' · ')}${acc.connected ? '' : ' (offline)'}`;
        },
        async fetchAccounts() {
            this.loadingAccounts = true;
            this.feedback = { type: '', text: '' };
            try {
                const { data } = await axios.get('https://ncht.bz/api/tg/client/accounts');
                this.accounts = data?.accounts || [];
                if (!this.form.accountId && this.accounts.length) {
                    this.form.accountId = this.accounts[0].accountId;
                }
            } catch (error) {
                this.feedback = {
                    type: 'error',
                    text: error.response?.data?.error || 'Не удалось загрузить список аккаунтов'
                };
            } finally {
                this.loadingAccounts = false;
            }
        },
        resetFeedback() {
            this.feedback = { type: '', text: '' };
        },
        buildPayload() {
            const payload = {
                accountId: this.form.accountId,
                message: this.form.message.trim()
            };

            if (this.identifierType === 'phone') {
                payload.phoneNumber = this.form.phoneNumber.trim();
                if (this.form.autoAddContact) {
                    payload.autoAddContact = true;
                    payload.firstName = this.form.firstName || 'User';
                    payload.lastName = this.form.lastName || '';
                }
            } else if (this.identifierType === 'username') {
                payload.username = this.form.username.trim();
            } else {
                payload.chatId = this.form.chatId.trim();
            }

            return payload;
        },
        async handleSend() {
            if (this.sendDisabled || this.sending) {
                return;
            }

            this.resetFeedback();
            this.sending = true;

            try {
                const payload = this.buildPayload();
                const { data } = await axios.post(
                    'https://ncht.bz/api/tg/client/send-message',
                    payload
                );
                this.feedback = { type: 'success', text: data?.message || 'Сообщение отправлено' };
            } catch (error) {
                const serverText = error.response?.data?.error || error.response?.data?.details;
                this.feedback = {
                    type: 'error',
                    text: serverText || 'Не удалось отправить сообщение, попробуйте позже'
                };
            } finally {
                this.sending = false;
            }
        }
    }
}
</script>

<style scoped>
.mailing-telegram {
    padding: 1.5rem;
}

.panel {
    border: 1px solid #dcdcdc;
    border-radius: 12px;
    padding: 1.5rem;
    background: #fff;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.panel__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.panel__header h3 {
    margin: 0;
}

.panel__subtitle {
    margin: 0.2rem 0 0;
    color: #6b7280;
    font-size: 0.9rem;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}

.field--half {
    grid-column: span 1;
}

.field--full {
    grid-column: span 2;
}

.hint {
    color: #6b7280;
    font-size: 0.85rem;
}

.checkbox-field {
    align-items: center;
    flex-direction: row;
    gap: 1rem;
}

.toggle-label {
    cursor: pointer;
}

.names-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
}

.feedback-group {
    min-height: 48px;
}

.w-full {
    width: 100%;
}

@media (max-width: 1024px) {
    .form-grid {
        grid-template-columns: 1fr;
    }

    .field--full,
    .field--half {
        grid-column: span 1;
    }
}
</style>
